import type { UserRoleDto } from "@/api/types";
import type { PermissionSelector } from "@/hooks/usePermission";

// Shared permission selectors consumed by multiple modules.
// Source modules re-export these; consumers import from here.

export const canListAnySurvey: PermissionSelector = (x) => x.surveys.canListAll || x.surveys.canManage;

export const canCreateInterestGroup: PermissionSelector = (x) => x.community.canCreateGroup;

export const canManageEvents: PermissionSelector = (x) => x.events.canManage;

export const canListAddresses: PermissionSelector = (x) => x.addressManagement.canListAddresses;

export const canManageCompanies: PermissionSelector = (x) => x.addressManagement.canManageCompanies;

export const canManageBuildings: PermissionSelector = (x) => x.addressManagement.canManageBuildings;

export const canEditOtherUserNotificationSettings: PermissionSelector = (x) =>
  x.userManagement.canEditNotificationSettings;

export const canManageProjectManagement: PermissionSelector = (x) => x.projectManagement.canManageAll;

export const canListCategories: PermissionSelector = (x) => x.isAdmin && x.repairManagement.canListCategories;

export const canListUsers: PermissionSelector = (x) => x.userManagement.canListUsers;

export const canManageAnyBookableAsset: PermissionSelector = (x) => x.assets.canManageAll || x.assets.canManageOwn;

export const canListMessages: PermissionSelector = (x) => x.canListPosts;

export const canViewSchedule: PermissionSelector = (x) => x.assets.canViewSchedule;

export const canListDocuments: PermissionSelector = (x) => x.canListDocuments || x.documents.canManageAll;

export const canManageAllDocuments: PermissionSelector = (x) => x.documents.canManageAll;

export const canViewProjectManagement: PermissionSelector = (x) => x.projectManagement.canViewAll;

export const canManageRoles: PermissionSelector = (x) => x.userManagement.canManageRoles;

export const canCreateStandardMessage: PermissionSelector = (x) => x.canCreateStandardPosts;

export const canCreateAnnouncementMessage: PermissionSelector = (x) => x.canCreateAnnouncements;

interface BaseRoleDto {
  roleId: string;
}

export type FormPermissions<T extends BaseRoleDto> = Record<
  string,
  T & { name: string; roleType: UserRoleDto["type"] }
>;

export function createFormPermissions<T extends BaseRoleDto>(
  roles: UserRoleDto[],
  defaultPermissions: T[],
  permissions: T[] | undefined,
): FormPermissions<T> {
  return Object.fromEntries(
    defaultPermissions.map((defaultPermission) => {
      const permission = permissions?.find(({ roleId }) => roleId === defaultPermission.roleId);
      const role = roles.find(({ id }) => id === defaultPermission.roleId);

      if (role == null) {
        throw new Error("Permissions are invalid");
      }

      const formPermission = {
        ...defaultPermission,
        ...permission,
        name: role.name,
        roleType: role.type,
      };

      return [formPermission.roleId, formPermission];
    }),
  );
}

export function toPermissionsRequest<T extends BaseRoleDto>(formPermissions: FormPermissions<T>): T[] {
  return Object.values(formPermissions);
}
